import Vue from 'vue';
import XLSX from 'xlsx';
import $ from 'jquery';
import { setColumnSize } from '@/utils/sheets';
import { i18n } from '@/plugins/i18n';
import { utilsTiptapEditor } from '@/plugins/tiptap';

export const pdcaWorkflow = () => [
  { id: 0, text: i18n.t('message.PDCA_BEFORE_P'), ref: 'pending' },
  { id: 1, text: i18n.t('message.PDCA_PLAN'), ref: 'plan' },
  { id: 2, text: i18n.t('message.PDCA_DO'), ref: 'do' },
  { id: 3, text: i18n.t('message.PDCA_CHECK'), ref: 'check' },
  { id: 4, text: i18n.t('message.PDCA_ACT'), ref: 'act' },
];

export const quickActionWorkflow = () => [
  { id: 0, text: i18n.t('message.ACTION_STATE_PENDING'), ref: 'pending' },
  { id: 1, text: i18n.t('message.ACTION_STATE_ONGOING'), ref: 'do' },
  { id: 2, text: i18n.t('message.ACTION_STATE_COMPLETED'), ref: 'act' },
];

/**
 * Retrieve the workflow of the action.
 * Workflow is different whether the action is a PDCA action (default) or a quick action
 * @param  {Object} action
 * @return {Array} Array of workflow step
 */
export function getActionWorkflow(action) {
  if (action.isQuickAction) {
    return quickActionWorkflow();
  }
  return pdcaWorkflow();
}

/**
 * Retrieve the step in the workflow of the action.
 * @param  {Object} action
 * @return {Object} Workflow step
 */
export function getActionWorkflowStep(action) {
  const workflow = getActionWorkflow(action);
  const idx = Number(action.pdca).sBetweenBoundaries(0, workflow.length - 1);
  return workflow[idx];
}

/**
 * Check if the action is at the last step of its workflow
 * @param  {Object}  action
 * @return {Boolean}
 */
export function isActionCompleted(action) {
  const workflow = getActionWorkflow(action);
  return Number(action.pdca) === workflow.length - 1;
}

/**
 * Create an excel sheet for a list of actions
 * @param  {Array} actions    List of actions
 */
export function exportActionsToExcel(actions) {
  const dateFormat = i18n.t('dateFormat');
  const no = i18n.t('message.NO');
  const yes = i18n.t('message.YES');
  const actionColumns = [
    i18n.t('message.TEAM'),
    i18n.t('message.ACTION'),
    i18n.t('message.NAME'),
    i18n.t('message.LEADER'),
    i18n.t('message.IMPACT'),
    i18n.t('message.PDCA'),
    i18n.t('message.ARCHIVED_THIS_WEEK'),
    i18n.t('message.DEADLINE'),
    i18n.t('message.ORIGINAL_DEADLINE'),
    i18n.t('message.POSTPONEMENTS_NUMBER'),
    i18n.t('message.POSTPONEMENTS_REASON'),
    i18n.t('message.COMMENTS'),
    i18n.t('message.PROBLEM'),
    i18n.t('message.ACTIONS_PROBLEM_WHAT'),
    i18n.t('message.CREATION_DATE'),
    i18n.t('message.WORK_START_DATE'),
    i18n.t('message.ACT_DATE'),
    i18n.t('message.ARCHIVE_DATE'),
  ];
  const actionRows = [actionColumns];
  const now = Vue.moment();
  actions.forEach((action) => {
    // Check if there is an originalDeadline value in the history
    const deadline = Vue.moment(action.deadline);
    const originalDeadline = Vue.moment(action.originalDeadline);
    const deadlineVal = deadline.isValid() ? deadline.format(dateFormat) : '';
    // Concatenate postponements reasons in a column
    const postpmtComments = action.postpmts
      .map((postpmt) => {
        const fDate = Vue.moment(postpmt.date).format(dateFormat);
        const reason = postpmt.comment === '' ? i18n.t('message.NO_POSTPONEMENT_REASON') : postpmt.comment;
        return `${i18n.t('message.POSTPONEMENT_REASON', { date: fDate })}${i18n.t('message.TWO_DOTS')}${reason}`;
      })
      .join('\n');
    // Archiving date
    const archiveDate = Vue.moment(action.completedOn);
    const archivedDateCol = archiveDate.isValid() ? archiveDate.format(dateFormat) : '';
    // Yes/No column to indicate whether this action was archived in the last 7 days
    const archivedThisWeek = !archiveDate.isValid() || now.diff(archiveDate, 'd') > 7 ? no : yes;
    // Creation date
    const createdDate = Vue.moment(action.created);
    const createdDateCol = createdDate.isValid() ? createdDate.format(dateFormat) : '';
    // Work start date
    const workStart = Vue.moment(action.workStarted);
    const workStartCol = workStart.isValid() ? workStart.format(dateFormat) : '';
    // Work start date
    const actedOn = Vue.moment(action.actedOn);
    const actedOnCol = actedOn.isValid() ? actedOn.format(dateFormat) : '';
    // Action rich-text comment
    let comment = '';
    let htmlComment = action.comment;
    // NOTE Prior to v1.7.39, tiptap content was stored in the DB as a JSON object, hence the JSON parsing.
    // From 1.7.39 onwards, HTML is prefered as it makes it easier to work with in the back-end.
    try {
      const commentObj = JSON.parse(action.comment);
      utilsTiptapEditor.commands.setContent(commentObj);
      htmlComment = utilsTiptapEditor.getHTML();
    } catch (e) {}

    const htmlElts = $.parseHTML(`<div>${htmlComment}</div>`);
    if (htmlElts && htmlElts.length === 1) {
      // innerText only works if the element is visible
      $(document.body).append(htmlElts[0]);
      comment = htmlElts[0].innerText;
      htmlElts[0].remove();
    }

    actionRows.push([
      action.teamName,
      action.id,
      action.name,
      i18n.t('fullNameFormat', { firstName: action.leaderFirstName, lastName: action.leaderLastName }),
      action.impact,
      action.state === 2 ? i18n.t('message.PDCA_ACT_ARCHIVED') : getActionWorkflowStep(action).text,
      archivedThisWeek,
      deadlineVal,
      originalDeadline.isValid() ? originalDeadline.format(dateFormat) : deadlineVal,
      action.postpmts.length,
      postpmtComments,
      comment,
      action.problemId,
      action.problemWhat,
      createdDateCol,
      workStartCol,
      actedOnCol,
      archivedDateCol,
    ]);
  });
  const wb = XLSX.utils.book_new();
  wb.Props = {
    Title: i18n.t('message.ACTIONS'),
    Subject: i18n.t('message.ACTIONS'),
    CreateDate: Vue.moment().format(dateFormat),
  };

  const actionsSheet = XLSX.utils.aoa_to_sheet(actionRows);
  setColumnSize(actionsSheet, 15);
  XLSX.utils.book_append_sheet(wb, actionsSheet, i18n.t('message.ACTIONS'));
  XLSX.writeFile(wb, `${i18n.t('message.ACTIONS')}_Shizen_${now.format(i18n.t('lFullDateFormat'))}.xlsx`);
}

export const actionPlanUnits = [
  { id: 0, unit: 'message.HOURS', nHours: 1 },
  { id: 1, unit: 'message.DAYS', nHours: 24 },
  { id: 2, unit: 'message.WEEKS', nHours: 168 },
];

export default {
  pdcaWorkflow,
  quickActionWorkflow,
  getActionWorkflow,
  getActionWorkflowStep,
  isActionCompleted,
  exportActionsToExcel,
  actionPlanUnits,
};
